import { useTranslation } from 'react-i18next'
import { X } from 'lucide-react'
import { Select } from '@/components/v2/ui/Select'
import { Button } from '@/components/v2/ui/Button'
import { SegmentRuleChannelPicker } from './SegmentRuleChannelPicker'
import { SegmentRuleValueControl } from './SegmentRuleValueControl'
import { CHANNEL_CAPABLE_FIELDS } from './segmentPresets'
import type { SegmentCondition, SegmentField } from './segmentPresets'

const BOOL_FIELDS: ReadonlySet<SegmentField> = new Set(['is_active', 'has_image', 'has_issues', 'listed_on_channel'])
const NUMERIC_CHANNEL_FIELDS: ReadonlySet<SegmentField> = new Set(['channel_quantity_30d', 'channel_revenue_30d'])
const FIELD_OPTIONS: SegmentField[] = [
  'is_active', 'has_image', 'has_issues',
  'quantity_30d_min', 'revenue_30d_min', 'quantity_90d_min', 'revenue_90d_min',
  'channel_quantity_30d', 'channel_revenue_30d', 'listed_on_channel',
]
// P2b-Felder tragen camelCase Label-Keys (Rest folgt 1:1 dem snake_case Feldnamen).
const FIELD_LABEL_KEYS: Partial<Record<SegmentField, string>> = {
  channel_quantity_30d: 'channelQuantity30d',
  channel_revenue_30d: 'channelRevenue30d',
  listed_on_channel: 'listedOnChannel',
  has_issues: 'hasIssues',
}

function defaultConditionForField(f: SegmentField): SegmentCondition {
  if (BOOL_FIELDS.has(f)) return { field: f, op: 'eq', value: false }
  return { field: f, op: 'gte', value: 0 }
}

interface SegmentRuleRowProps {
  condition: SegmentCondition
  onChange: (c: SegmentCondition) => void
  onRemove: () => void
}

/** Single editable condition row. Field determines op (eq=bool, gte/lte=numeric) + optional channel_id. */
export function SegmentRuleRow({ condition, onChange, onRemove }: SegmentRuleRowProps) {
  const { t } = useTranslation()
  const { field, channel_id } = condition
  const isListedOnChannel = field === 'listed_on_channel'
  const isBool = BOOL_FIELDS.has(field) && !isListedOnChannel
  const isNumericChannel = NUMERIC_CHANNEL_FIELDS.has(field)
  const needsChannel = CHANNEL_CAPABLE_FIELDS.has(field)

  const fieldOptions = FIELD_OPTIONS.map((f) => ({
    value: f,
    label: t(`productSegments.fields.${FIELD_LABEL_KEYS[f] ?? f}`),
  }))

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-wrap items-center gap-2 w-full">
        <div className="flex-1 min-w-0">
          <Select
            data-testid="rule-field"
            options={fieldOptions}
            value={field}
            onValueChange={(v) => onChange(defaultConditionForField(v as SegmentField))}
            aria-label={t('productSegments.builder.fieldLabel')}
          />
        </div>

        <SegmentRuleValueControl
          condition={condition}
          isBool={isBool}
          isListedOnChannel={isListedOnChannel}
          isNumericChannel={isNumericChannel}
          onChange={onChange}
        />

        <Button
          data-testid="rule-remove"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label={t('productSegments.builder.removeCondition')}
        >
          <X size={16} aria-hidden="true" />
        </Button>
      </div>

      {needsChannel && (
        <SegmentRuleChannelPicker
          channelId={channel_id}
          onChange={(id) => onChange({ ...condition, channel_id: id })}
        />
      )}
    </div>
  )
}

SegmentRuleRow.displayName = 'SegmentRuleRow'
